import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { BrandEnrichmentResult } from '@agency-smm/shared';
import { Modal, ModalActions } from '../Modal';
import { Button } from '../Button';
import { UseBrandFormResult } from '../../hooks/brand/useBrandForm';
import {
  EnrichDiffItem,
  EnrichGroup,
  computeEnrichmentDiff,
  applyEnrichmentDiff,
} from './brandEnrichment';

// Modal de revisión del diff del enriquecimiento. Muestra, agrupado, qué propone
// la IA a partir de las fuentes y deja aceptar/rechazar campo por campo. No pisa
// nada hasta que el usuario aplica; y aún así solo mergea sobre el form (el Save
// sigue siendo explícito).

const GROUP_ORDER: EnrichGroup[] = ['Identity', 'Personality', 'Voice', 'Content', 'Contact'];

interface Props {
  isOpen: boolean;
  result: BrandEnrichmentResult | null;
  form: UseBrandFormResult;
  onClose: () => void;
  onApplied: (count: number) => void;
}

const Intro = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  line-height: 1.5;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const GroupTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${({ theme }) => theme.colors.textMuted};
  margin: ${({ theme }) => theme.spacing.lg} 0 ${({ theme }) => theme.spacing.sm};
`;

const Item = styled.label<{ $on: boolean }>`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm};
  border: 1px solid ${({ theme, $on }) => ($on ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  cursor: pointer;
  background: ${({ theme, $on }) => ($on ? `${theme.colors.primary}0d` : theme.colors.surface)};
  transition: border-color 0.15s ease, background 0.15s ease;
`;

const Check = styled.input`
  margin-top: 3px;
  width: 16px;
  height: 16px;
  cursor: pointer;
  flex-shrink: 0;
`;

const ItemBody = styled.div`
  flex: 1;
  min-width: 0;
`;

const ItemHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ItemLabel = styled.span`
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
`;

const ActionPill = styled.span<{ $action: string }>`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 6px;
  border-radius: 4px;
  color: white;
  background: ${({ theme, $action }) =>
    $action === 'replace' ? '#D97706' : $action === 'add' ? theme.colors.success : theme.colors.primary};
`;

const Suggested = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  margin-top: 4px;
  word-break: break-word;
`;

const CurrentLine = styled.p`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: 2px;
  text-decoration: line-through;
  word-break: break-word;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-top: 4px;
`;

const Chip = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs};
  padding: 2px 8px;
  border-radius: ${({ theme }) => theme.borderRadius.sm};
  background: ${({ theme }) => theme.colors.background};
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.text};
`;

const Empty = styled.p`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  text-align: center;
  padding: ${({ theme }) => theme.spacing.lg} 0;
`;

const labelKey = (item: EnrichDiffItem) => item.key as string;

export function EnrichReviewModal({ isOpen, result, form, onClose, onApplied }: Props) {
  const [items, setItems] = useState<EnrichDiffItem[]>([]);

  // Recalcula el diff solo cuando llega un resultado nuevo (no en cada toggle,
  // así no perdemos los tildes del usuario).
  useEffect(() => {
    if (result) setItems(computeEnrichmentDiff(result.fields, form.formData));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  const acceptedCount = useMemo(() => items.filter((i) => i.accepted).length, [items]);

  const grouped = useMemo(() => {
    const map = new Map<EnrichGroup, EnrichDiffItem[]>();
    for (const it of items) {
      const arr = map.get(it.group) || [];
      arr.push(it);
      map.set(it.group, arr);
    }
    return GROUP_ORDER.filter((g) => map.has(g)).map((g) => ({ group: g, items: map.get(g)! }));
  }, [items]);

  const toggle = (key: string) => {
    setItems((prev) => prev.map((i) => (labelKey(i) === key ? { ...i, accepted: !i.accepted } : i)));
  };

  const apply = () => {
    const count = applyEnrichmentDiff(form, items);
    onApplied(count);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Review enrichment" maxWidth="640px">
      {items.length === 0 ? (
        <Empty>Your sources didn't add anything new compared to what you already have. 👌</Empty>
      ) : (
        <>
          <Intro>
            The AI read your sources and proposes these changes. Items that <strong>fill</strong> empty fields or{' '}
            <strong>add</strong> to lists are pre-checked; the ones that <strong>replace</strong> existing text are
            left to you. Nothing is saved until you apply and then press <strong>Save</strong>.
          </Intro>

          {grouped.map(({ group, items: groupItems }) => (
            <div key={group}>
              <GroupTitle>{group}</GroupTitle>
              {groupItems.map((item) => (
                <Item key={labelKey(item)} $on={item.accepted}>
                  <Check
                    type="checkbox"
                    checked={item.accepted}
                    onChange={() => toggle(labelKey(item))}
                  />
                  <ItemBody>
                    <ItemHead>
                      <ItemLabel>{item.label}</ItemLabel>
                      <ActionPill $action={item.action}>
                        {item.action === 'fill' ? 'Fills' : item.action === 'replace' ? 'Replaces' : 'Adds'}
                      </ActionPill>
                    </ItemHead>
                    {item.type === 'string' ? (
                      <>
                        <Suggested>{item.suggested}</Suggested>
                        {item.action === 'replace' && item.current && <CurrentLine>{item.current}</CurrentLine>}
                      </>
                    ) : (
                      <Chips>
                        {(item.newItems || []).map((v, i) => <Chip key={i}>{v}</Chip>)}
                      </Chips>
                    )}
                  </ItemBody>
                </Item>
              ))}
            </div>
          ))}
        </>
      )}

      <ModalActions>
        <Button type="button" $variant="outline" onClick={onClose}>Cancel</Button>
        {items.length > 0 && (
          <Button type="button" onClick={apply} disabled={acceptedCount === 0}>
            Apply {acceptedCount} change{acceptedCount === 1 ? '' : 's'}
          </Button>
        )}
      </ModalActions>
    </Modal>
  );
}
